import {
  buildForm,
  buildImage,
  buildLink,
} from '@amazeelabs/react-framework-bridge/gatsby';
import { useLocale } from '@nuklear9/i18n';
import { Company, CompanyItem } from '@nuklear9/ui';
import React, { ComponentProps } from 'react';
import * as yup from 'yup';

import { useCompaniesListQuery } from '../../generated/queries/react-queries';
import { useBranches } from '.';
import { drupalImageData } from './drupal-image';
import { formNavigate } from './form-navigate';
import { paginationSchema, usePagination } from './use-pagination';
import { useQueryParameters } from './use-query-parameters';

const companySearchSchema = paginationSchema.shape({
  title: yup.string().min(3).default(''),
  industry: yup.string().default(''),
});

const useCompanyParameters = () =>
  useQueryParameters<yup.InferType<typeof companySearchSchema>>(
    companySearchSchema,
  );

const useContentSearchForm = () => {
  return buildForm({
    initialValues: useCompanyParameters(),
    onSubmit: formNavigate,
  });
};

const useCompanySearch: ComponentProps<typeof Company>['useCompanySearch'] = (
  itemsPerPage,
) => {
  const params = useCompanyParameters();

  const { data, isFetching, status } = useCompaniesListQuery({
    input: {
      page: params.page - 1,
      itemsPerPage: itemsPerPage,
      filters: {
        title: params.title,
        branch: params.industry ? [params.industry] : [],
      },
      langcode: useLocale(),
    },
  });

  const items = data?.companies.content
    ? data?.companies.content.map(
        ({
          path,
          companyName,
          city,
          countryCode,
          description,
          logo,
        }): ComponentProps<typeof CompanyItem> => ({
          Link: buildLink({ href: path }),
          description: description || undefined,
          countryCode: countryCode || undefined,
          city: city || undefined,
          companyName,
          Image: logo
            ? buildImage({
                image: drupalImageData(
                  logo as ImageFragment,
                  'content',
                  '410px',
                ),
                objectFit: 'contain',
                alt: logo.alt || companyName,
              })
            : undefined,
        }),
      )
    : [];

  return {
    isFetching,
    status,
    data: {
      items,
      total: data?.companies.total || 0,
    },
  };
};

const CompaniesList = ({ pageContext }: any) => {
  const branches = useBranches(pageContext.locale);

  return (
    <Company
      industries={
        branches
          ? branches.map(({ drupalId, title }) => ({
              value: drupalId,
              title,
            }))
          : undefined
      }
      usePagination={usePagination}
      useCompanySearch={useCompanySearch}
      useForm={useContentSearchForm}
    />
  );
};

export default CompaniesList;
